import React from 'react';

/**
 * Route code-splitting with warm-up.
 *
 * Every page lives in its own chunk (React.lazy) so the main bundle only
 * carries the shell. To keep navigation feeling instant, chunks are fetched
 * BEFORE the click: on sidebar/link hover or focus (`preloadRoute`), for the
 * URL the app booted on (`preloadCurrentRoute`), and for the common pages
 * once the browser goes idle after first paint (`preloadOnIdle`).
 */

const routes = [];

/**
 * React.lazy with a `.preload()` that starts (and memoizes) the chunk fetch.
 * A failed import is forgotten so the next attempt retries instead of
 * replaying the rejection (deploys rotate chunk hashes under open tabs).
 */
export function lazyWithPreload(factory) {
  let promise = null;
  const load = () => {
    if (!promise) {
      promise = factory().catch((err) => {
        promise = null;
        throw err;
      });
    }
    return promise;
  };
  const Component = React.lazy(load);
  Component.preload = load;
  return Component;
}

/**
 * Registers a path prefix → chunk loader(s). Prefixes match on segment
 * boundaries ('/reports/financial' covers '/reports/financial/pnl').
 */
export function registerRoutePreload(prefix, ...components) {
  routes.push({ prefix, load: () => Promise.all(components.map((c) => c.preload?.())) });
  // Longest prefix first so '/dashboard/night-audit' beats '/dashboard'.
  routes.sort((a, b) => b.prefix.length - a.prefix.length);
}

const pathOf = (to) => {
  if (!to) return '';
  const s = typeof to === 'string' ? to : to.pathname || '';
  return s.split(/[?#]/)[0];
};

export function preloadRoute(to) {
  const path = pathOf(to);
  if (!path) return;
  const hit = routes.find(({ prefix }) => path === prefix || path.startsWith(`${prefix}/`));
  if (hit) hit.load().catch(() => { /* the real navigation retries and surfaces it */ });
}

export function preloadCurrentRoute() {
  if (typeof window === 'undefined') return;
  preloadRoute(window.location.pathname === '/' ? '/dashboard' : window.location.pathname);
}

/** Warm the given paths one at a time once the main thread is idle. */
export function preloadOnIdle(paths, { delay = 2500 } = {}) {
  if (typeof window === 'undefined') return;
  // Respect data-saver / slow connections.
  const conn = navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
  const queue = [...paths];
  const next = () => {
    const p = queue.shift();
    if (!p) return;
    preloadRoute(p);
    idle(next, { timeout: 4000 });
  };
  setTimeout(() => idle(next, { timeout: 4000 }), delay);
}

/**
 * Data warm-up for report links: once the pointer rests on a link, the page's
 * default first query is prefetched into the report cache so the click paints
 * from it. The app feeds the org/settings it needs (setRoutePrefetchContext);
 * the helper module loads on demand to stay out of the main bundle.
 */
let prefetchContext = null;
export function setRoutePrefetchContext(ctx) {
  prefetchContext = ctx;
}

const DATA_INTENT_MS = 150;
let intentTimer = null;

function prefetchRouteData(to) {
  const path = pathOf(to);
  // Clean links only: a query string means non-default params.
  if (!path.startsWith('/reports/') || (typeof to === 'string' && to.includes('?'))) return;
  if (!prefetchContext?.organization?.id || !prefetchContext?.userSettings) return;
  const ctx = prefetchContext;
  import('../pages/reports/reportPrefetch')
    .then((m) => m.prefetchReportView(path, ctx))
    .catch(() => { /* best-effort */ });
}

/** Spread onto a <Link> so hover / keyboard focus starts the chunk fetch. */
export const preloadHandlers = (to) => ({
  onMouseEnter: () => {
    preloadRoute(to);
    clearTimeout(intentTimer);
    intentTimer = setTimeout(() => prefetchRouteData(to), DATA_INTENT_MS);
  },
  onMouseLeave: () => clearTimeout(intentTimer),
  onFocus: () => {
    preloadRoute(to);
    prefetchRouteData(to);
  },
});
